import React, { useEffect, useState } from "react";
import { permissionGroups } from "./permissionsConfig";
import { CircleX, Pencil } from "lucide-react";
import FilterDropdown from "../../components/UIElements/Dropdown";
import { getData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { confirmAction } from "../../utils/utils";
import { AdminUser } from "../../models/AdminUser";
import InstructorAccessController from "./InstructorAccessController";

interface PermissionsGridProps {
  permissions: string[]; // e.g. formData.permissions
  setPermissions: (updated: string[]) => void; // a callback to update permissions
  setFormData: React.Dispatch<React.SetStateAction<AdminUser>>;
  formData: AdminUser
}

const PermissionsGrid: React.FC<PermissionsGridProps> = ({
  permissions,
  setPermissions,
  setFormData,
  formData,
}) => {
  
  const SUB_PERMISSION_KEY_MAP: { [key: string]: string } = {
    "DOT Compliance": "dotPermissions",
    "Human Resources": "hrPermissions",
    "Biz Partners": "bizPermissions",
    "TMS Users": "tmsPermissions",
    "Courses":"coursesPermissions"
  };

  const toggleGroup = (name: string) => {
    setPermissions(
      permissions.includes(name)
        ? permissions.filter((p) => p !== name)
        : [...permissions, name]
    );
  };

  const toggleSubpermission = (groupName: string, subPermission: string) => {
    const key = SUB_PERMISSION_KEY_MAP[groupName];
    if (!key) return;
    const currentSubPermissions = formData[
      key as keyof typeof formData
    ] as string[];
    const updatedSubPermissions = currentSubPermissions.includes(subPermission)
      ? currentSubPermissions.filter((sp) => sp !== subPermission)
      : [...currentSubPermissions, subPermission];
    setFormData((prev) => ({
      ...prev,
      [key]: updatedSubPermissions,
    }));
  };
  const getSubPermissionsArray = (groupName: string): string[] => {
    const key =
      SUB_PERMISSION_KEY_MAP[groupName as keyof typeof SUB_PERMISSION_KEY_MAP];

    if (!key) return [];
    return (formData[key as keyof typeof formData] as string[]) || [];
  };

  const [currentOpen, setCurrentOpen] = useState("");
  const [users, setAllUsers] = useState<
    {
      _id: string;
      name: string;
      email: string;
      phoneNumber: string;
      password: string;
      permissions: never[];
      adminType: string;
    }[]
  >([]);

  const getAllUsers = async () => {
    const response = await getData(ApiUrl.listAdmins, {});
    setAllUsers(response.data);
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 px-3">
      {permissionGroups.map((group) => (
        <>
          {group.name === "Instructors" ? (
            <div className="col-span-full">
              {/* Instructor here */}
              <InstructorAccessController setFormData={setFormData} formData={formData}   ></InstructorAccessController>
            </div>
          ) :
            <div
              key={group.name}
              className="border rounded-xl shadow-sm p-4 bg-white"
            >
              <label className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 rounded accent-greenBg"
                  checked={permissions.includes(group.name)}
                  onChange={() =>
                    confirmAction(
                      `Are you sure you want to ${permissions.includes(group.name) ? "remove" : "give"
                      } access`,
                      () => toggleGroup(group.name)
                    )
                  }
                />
                <span className="font-bold">{group.name}</span>
              </label>
              <ul className="pl-6 text-gray-700 text-sm space-y-1">
                {group.name !== "Instructors" &&
                  group.subPermissions.map((sub) => (
                    <div className="flex items-center" key={sub}>
                      {group.isSubPermission && (
                        <input
                          type="checkbox"
                          className="accent-greenBg"
                          onChange={() =>
                            confirmAction(
                              `Are you sure you want to ${getSubPermissionsArray(group.name).includes(sub)
                                ? "remove"
                                : "give"
                              } sub-permission`,
                              () => toggleSubpermission(group.name, sub)
                            )
                          }
                          checked={getSubPermissionsArray(group.name).includes(sub)}
                        />
                      )}
                      <p className="pl-2">{sub}</p>
                    </div>
                  ))}


              </ul>
            </div>}
        </>
      ))}
    </div>
  );
};

export default PermissionsGrid;
